import { Step } from '~components/wizard-form/props';
import BudgetStep from '~support-at-home/components/config/service-cutover/BudgetStep';
import CutoverIntro from '~support-at-home/components/config/service-cutover/CutoverIntro';
import CutoverSummary from '~support-at-home/components/config/service-cutover/CutoverSummary';
import PricePlanStep from '~support-at-home/components/config/service-cutover/PricePlanStep';
import SelectServicePairs from '~support-at-home/components/config/service-cutover/SelectServicePairs';
import ServiceTypeStep from '~support-at-home/components/config/service-cutover/ServiceTypeStep';

import { CutoverPricePlanResult, CutoverServiceTypeResult } from '~support-at-home/actions/sahServiceCutover';

export interface CutoverResults {
  applied_service_types?: CutoverServiceTypeResult;
  applied_price_plans?: CutoverPricePlanResult;
  /** The budget work runs in the background - the last step watches this run rather than a result. */
  budget_run_id?: string;
  onRunningChange?: (isRunning: boolean) => void;
}

/**
 * The service cutover assistant, as a wizard.
 *
 * Steps 2, 3 and 4 are each COMMITTED WHEN THEY ARE CONFIRMED rather than held and applied at the
 * end. That is a deliberate departure from the preview-then-apply-once shape of the budget bulk
 * copy, and it is what lets a provider stop part way through with the work already done standing.
 *
 * Back is offered between a step's selection and its confirmation, so a provider who realises the
 * selection is wrong can retreat and fix it. It writes nothing: a step applies on the way FORWARD out
 * of it only, which is what the direction argument to `beforeStepChange` is for.
 *
 * What Back cannot do is undo. Stepping back past a step already confirmed shows it again with its
 * work reported as already done - the same thing a re-run shows - rather than reversing it. The last
 * step has no Back at all, because by then every step has been applied and there is nothing left to
 * reconsider.
 */
const cutoverSteps = (results: CutoverResults): Step[] => {
  return [
    {
      step: 1,
      title: 'Services',
      fields: {
        // Order 0 so it sits above the cutover date as well as the criteria - the explanation of what
        // the assistant is comes before the first thing it asks for, not between its questions.
        intro: {
          type: 'custom',
          component: CutoverIntro,
          isAttribute: false,
          colSize: 12,
          order: 0,
        },
        cutover_date: {
          type: 'date',
          field: 'cutover_date',
          caption: 'Cutover date',
          required: true,
          colSize: 4,
          order: 1,
        },
        pairs: {
          type: 'custom',
          component: SelectServicePairs,
          dependencies: ['cutover_date', 'pairs'],
          isAttribute: false,
          colSize: 12,
          order: 2,
        },
      },
      // Nothing downstream means anything without at least one replacement, and `resolve` refuses a
      // run with none - so the gate is here rather than as a server error the provider has to read.
      disableNext: ({ values }) => !(values?.pairs?.length > 0),
      showPrevButton: false,
      nextButtonCaption: 'Start',
    },
    {
      step: 2,
      title: 'Service Types',
      fields: {
        service_types: {
          type: 'custom',
          component: ServiceTypeStep,
          dependencies: ['cutover_date', 'pairs', 'service_type_ids'],
          isAttribute: false,
          colSize: 12,
        },
      },
      showPrevButton: true,
      prevButtonCaption: 'Back',
      nextButtonCaption: 'Confirm associations',
      confirmNext: ({ values }) => ({
        messages: [
          `Associate ${values?.service_types_pending ?? 0} service type(s) with the replacement service.`,
          'This is applied now, and stands whether or not you complete the remaining steps.',
        ],
      }),
    },
    {
      step: 3,
      title: 'Price Plans',
      fields: {
        price_plans: {
          type: 'custom',
          component: PricePlanStep,
          dependencies: ['cutover_date', 'pairs', 'service_type_ids', 'plan_ids'],
          isAttribute: false,
          colSize: 12,
        },
      },
      showPrevButton: true,
      prevButtonCaption: 'Back',
      nextButtonCaption: 'Confirm prices',
      confirmNext: ({ values }) => ({
        messages: [
          `Add a new revision to ${values?.price_plans_pending ?? 0} price plan(s), carrying the replacement at the same prices.`,
          'Prices are copied as they stand. To charge a different price, edit the new revision afterwards.',
          'This is applied now, and stands whether or not you complete the remaining step.',
        ],
      }),
    },
    {
      step: 4,
      title: 'Budgets',
      fields: {
        budgets: {
          type: 'custom',
          component: BudgetStep,
          dependencies: ['cutover_date', 'pairs', 'service_type_ids', 'budget_ids', 'status_ids'],
          isAttribute: false,
          colSize: 12,
        },
      },
      showPrevButton: true,
      prevButtonCaption: 'Back',
      nextButtonCaption: 'Confirm budgets',
      // Nothing left to move means every budget found is either unticked or unable to be moved, and
      // starting the run would only earn a refusal from the server.
      disableNext: ({ values }) => !(values?.budgets_pending > 0),
      confirmNext: ({ values }) => ({
        messages: [
          `Bring ${values?.budgets_pending ?? 0} budget(s) into line with the replacement service.`,
          'Each affected line ends the day before the cutover and the replacement picks up the dates it gives up.',
          'Each budget is flagged Needs Review. Open it, check it and save it to clear the flag.',
        ],
      }),
    },
    {
      step: 5,
      title: 'Done',
      fields: {
        summary: {
          type: 'custom',
          component: CutoverSummary,
          isAttribute: false,
          colSize: 12,
          deriveValue: () => results,
        },
      },
      showPrevButton: false,
      // No way out while budgets are still being changed. The run survives the modal closing, but
      // offering Close mid-run invites a provider to walk away from the only view of it.
      showNextButton: ({ values }) => !values?.budget_run_in_progress,
    },
  ];
};

export default cutoverSteps;
